import {
  NutritionDeclarationType,
  FoodType,
  RecipeType,
  FoodUserFriendlyUnitType,
} from "../model/nutritionPlanTypes";
import { User_Friendly_Units } from "../model/userFriendlyUnits";

/**
 * Nutrition Calculations Helper
 *
 * Provides utility functions for calculating nutritional values based on serving sizes.
 * All calculations are based on the 100g baseline system where initial_* values represent
 * nutritional content per 100 grams.
 *
 * Key Concepts:
 * - All initial_* values are per 100g
 * - User-friendly units have food-specific gram conversions
 * - Recipes don't have user-friendly units, only portion scaling
 */

/**
 * Converts user-friendly unit quantity to grams
 *
 * @param quantity - Amount in user-friendly units (e.g., 2 cups)
 * @param selectedUnit - The selected user-friendly unit with gram conversion
 * @returns Total grams
 *
 * @example
 * // If rice has 1 cup = 195g, and user selects 2 cups:
 * const grams = convertToGrams(2, riceUnit); // Returns 390g
 */
export const convertToGrams = (
  quantity: number,
  selectedUnit: FoodUserFriendlyUnitType
): number => {
  return quantity * selectedUnit.grams;
};

/**
 * Calculates nutritional values for a food item based on actual grams
 * Uses the proportion formula: (nutrition_per_100g * actual_grams) / 100
 *
 * @param food - Food item with initial_* baseline values (per 100g)
 * @param actualGrams - Actual serving size in grams
 * @returns Complete nutritional declaration for the serving
 *
 * @example
 * // Calculate nutrition for 390g of rice (initial values are per 100g)
 * const nutrition = calculateFoodNutrition(rice, 390);
 */
export const calculateFoodNutrition = (
  food: FoodType,
  actualGrams: number
): NutritionDeclarationType => {
  const ratio = actualGrams / 100;

  return {
    quantity: actualGrams,
    cals: Math.round(food.initial_cals * ratio),
    carbs: Math.round(food.initial_carbs * ratio * 10) / 10,
    protein: Math.round(food.initial_protein * ratio * 10) / 10,
    fat: Math.round(food.initial_fat * ratio * 10) / 10,
    fibers_grams: Math.round(food.initial_fibers_grams * ratio * 10) / 10,
    calcium_milligrams:
      Math.round(food.initial_calcium_milligrams * ratio * 10) / 10,
    vitamin_d_micrograms:
      Math.round(food.initial_vitamin_d_micrograms * ratio * 10) / 10,
    omega3_grams: Math.round(food.initial_omega3_grams * ratio * 10) / 10,
    iron_milligrams: Math.round(food.initial_iron_milligrams * ratio * 10) / 10,
    cholesterol_milligrams:
      Math.round(food.initial_cholesterol_milligrams * ratio * 10) / 10,
    sugars_grams: Math.round(food.initial_sugars_grams * ratio * 10) / 10,
    saturated_fat_grams:
      Math.round(food.initial_saturated_fat_grams * ratio * 10) / 10,
    iodine_micrograms:
      Math.round(food.initial_iodine_micrograms * ratio * 10) / 10,
    sodium_milligrams:
      Math.round(food.initial_sodium_milligrams * ratio * 10) / 10,
    potassium_milligrams:
      Math.round(food.initial_potassium_milligrams * ratio * 10) / 10,
    magnesium_milligrams:
      Math.round(food.initial_magnesium_milligrams * ratio * 10) / 10,
    vitamin_c_milligrams:
      Math.round(food.initial_vitamin_c_milligrams * ratio * 10) / 10,
    phenols_milligrams:
      Math.round(food.initial_phenols_milligrams * ratio * 10) / 10,
    antioxidants_milligrams:
      Math.round(food.initial_antioxidants_milligrams * ratio * 10) / 10,
    preservatives_milligrams:
      Math.round(food.initial_preservatives_milligrams * ratio * 10) / 10,
  };
};

/**
 * Calculates nutritional values for a recipe using ingredient-level scaling
 * Following SERVING_SIZE_DOCUMENTATION: scales each ingredient individually then sums
 *
 * IMPORTANT: This assumes ingredient quantities (ingredient.food.quantity) represent
 * the amounts for ONE PORTION of the recipe. If they represent total recipe amounts,
 * this will cause incorrect scaling.
 *
 * @param recipe - Recipe with ingredients array
 * @param servingMultiplier - Serving size multiplier (e.g., 1.5 for 1.5 servings)
 * @returns Complete nutritional declaration calculated from scaled ingredients
 *
 * @example
 * // Calculate nutrition for 1.5 servings of pasta dish (scales each ingredient)
 * const nutrition = calculateRecipeNutrition(pastaDish, 1.5);
 */
export const calculateRecipeNutrition = (
  recipe: RecipeType,
  servingMultiplier: number
): NutritionDeclarationType => {
  // Initialize totals
  const totals: NutritionDeclarationType = {
    quantity: 0,
    cals: 0,
    carbs: 0,
    protein: 0,
    fat: 0,
    fibers_grams: 0,
    calcium_milligrams: 0,
    vitamin_d_micrograms: 0,
    omega3_grams: 0,
    iron_milligrams: 0,
    cholesterol_milligrams: 0,
    sugars_grams: 0,
    saturated_fat_grams: 0,
    iodine_micrograms: 0,
    sodium_milligrams: 0,
    potassium_milligrams: 0,
    magnesium_milligrams: 0,
    vitamin_c_milligrams: 0,
    phenols_milligrams: 0,
    antioxidants_milligrams: 0,
    preservatives_milligrams: 0,
  };

  // Scale each ingredient individually and sum
  recipe.items.forEach((ingredient) => {
    if (ingredient.food) {
      // Get ingredient's original quantity in grams
      const originalGrams = ingredient.food.quantity || 100; // Default to 100g if not specified

      // Scale ingredient quantity
      const scaledGrams = originalGrams * servingMultiplier;

      // Calculate nutrition for this scaled ingredient
      const ingredientNutrition = calculateFoodNutrition(
        ingredient.food,
        scaledGrams
      );

      // Add to totals
      totals.quantity += ingredientNutrition.quantity;
      totals.cals += ingredientNutrition.cals;
      totals.carbs += ingredientNutrition.carbs;
      totals.protein += ingredientNutrition.protein;
      totals.fat += ingredientNutrition.fat;
      totals.fibers_grams += ingredientNutrition.fibers_grams;
      totals.calcium_milligrams += ingredientNutrition.calcium_milligrams;
      totals.vitamin_d_micrograms += ingredientNutrition.vitamin_d_micrograms;
      totals.omega3_grams += ingredientNutrition.omega3_grams;
      totals.iron_milligrams += ingredientNutrition.iron_milligrams;
      totals.cholesterol_milligrams +=
        ingredientNutrition.cholesterol_milligrams;
      totals.sugars_grams += ingredientNutrition.sugars_grams;
      totals.saturated_fat_grams += ingredientNutrition.saturated_fat_grams;
      totals.iodine_micrograms += ingredientNutrition.iodine_micrograms;
      totals.sodium_milligrams += ingredientNutrition.sodium_milligrams;
      totals.potassium_milligrams += ingredientNutrition.potassium_milligrams;
      totals.magnesium_milligrams += ingredientNutrition.magnesium_milligrams;
      totals.vitamin_c_milligrams += ingredientNutrition.vitamin_c_milligrams;
      totals.phenols_milligrams += ingredientNutrition.phenols_milligrams;
      totals.antioxidants_milligrams +=
        ingredientNutrition.antioxidants_milligrams;
      totals.preservatives_milligrams +=
        ingredientNutrition.preservatives_milligrams;
    }
  });

  // Round final totals for display
  return {
    quantity: Math.round(totals.quantity),
    cals: Math.round(totals.cals),
    carbs: Math.round(totals.carbs * 10) / 10,
    protein: Math.round(totals.protein * 10) / 10,
    fat: Math.round(totals.fat * 10) / 10,
    fibers_grams: Math.round(totals.fibers_grams * 10) / 10,
    calcium_milligrams: Math.round(totals.calcium_milligrams * 10) / 10,
    vitamin_d_micrograms: Math.round(totals.vitamin_d_micrograms * 10) / 10,
    omega3_grams: Math.round(totals.omega3_grams * 10) / 10,
    iron_milligrams: Math.round(totals.iron_milligrams * 10) / 10,
    cholesterol_milligrams: Math.round(totals.cholesterol_milligrams * 10) / 10,
    sugars_grams: Math.round(totals.sugars_grams * 10) / 10,
    saturated_fat_grams: Math.round(totals.saturated_fat_grams * 10) / 10,
    iodine_micrograms: Math.round(totals.iodine_micrograms * 10) / 10,
    sodium_milligrams: Math.round(totals.sodium_milligrams * 10) / 10,
    potassium_milligrams: Math.round(totals.potassium_milligrams * 10) / 10,
    magnesium_milligrams: Math.round(totals.magnesium_milligrams * 10) / 10,
    vitamin_c_milligrams: Math.round(totals.vitamin_c_milligrams * 10) / 10,
    phenols_milligrams: Math.round(totals.phenols_milligrams * 10) / 10,
    antioxidants_milligrams:
      Math.round(totals.antioxidants_milligrams * 10) / 10,
    preservatives_milligrams:
      Math.round(totals.preservatives_milligrams * 10) / 10,
  };
};

/**
 * Universal nutrition calculator for both foods and recipes
 * Automatically detects item type and applies appropriate calculations
 *
 * @param item - Food or recipe item
 * @param quantity - Quantity in user-friendly units (for foods) or serving multiplier (for recipes)
 * @param selectedUnit - Selected user-friendly unit (only for foods)
 * @returns Complete nutritional declaration
 *
 * @example
 * // For a food with user-friendly units
 * const foodNutrition = calculateNutrition(rice, 2, cupUnit);
 *
 * // For a recipe (no units needed)
 * const recipeNutrition = calculateNutrition(pastaDish, 1.5);
 */
export const calculateNutrition = (
  item: FoodType | RecipeType,
  quantity: number,
  selectedUnit?: FoodUserFriendlyUnitType
): NutritionDeclarationType => {
  // Check if item is a recipe by presence of 'items' property
  const isRecipe = "items" in item;

  if (isRecipe) {
    return calculateRecipeNutrition(item as RecipeType, quantity);
  } else {
    // For foods, selectedUnit is required
    if (!selectedUnit) {
      throw new Error("selectedUnit is required for food items");
    }
    const actualGrams = convertToGrams(quantity, selectedUnit);
    return calculateFoodNutrition(item as FoodType, actualGrams);
  }
};

/**
 * Gets user-friendly unit information by ID
 *
 * @param unitId - The numeric ID of the user-friendly unit
 * @returns Unit information with label and machine name
 *
 * @example
 * const cupUnit = getUserFriendlyUnitById(1); // Returns cup unit info
 */
export const getUserFriendlyUnitById = (unitId: number) => {
  return User_Friendly_Units[unitId] || null;
};

/**
 * Finds a specific user-friendly unit for a food item
 *
 * @param food - Food item with user_friendly_units array
 * @param unitId - The unit ID to find
 * @returns The matching FoodUserFriendlyUnitType or null
 *
 * @example
 * const riceUnit = findFoodUnit(rice, 1); // Find cup unit for rice
 */
export const findFoodUnit = (
  food: FoodType,
  unitId: number
): FoodUserFriendlyUnitType | null => {
  return (
    food.user_friendly_units.find(
      (unit) => unit.user_friendly_unit === unitId
    ) || null
  );
};
